'use client';

/* eslint-disable @next/next/no-img-element */
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LearningLessonDto, LessonBlockDto, UserDto, VocabEntryDto } from '@vuganawe/types';
import { RESOURCE_TYPE_LABELS } from '@vuganawe/types';
import { DictationBlock, GapFillBlock, MatchingBlock, SpeakingBlock } from '@/components/learning/exercises';
import { useT } from '@/i18n/client';
import {
  getLearningModuleWithAuth,
  saveCourseRating,
  saveLessonFavorite,
  saveLessonProgress,
  saveQuizAttempt,
  submitInquiry,
} from '@/lib/api/public';
import { getLearnerToken, getLearnerUser } from '@/lib/learnerSession';
import { enqueueWrite, isNetworkError } from '@/lib/offline';
import { playStep } from '@/lib/sound';
import { awardStepXp } from '@/lib/gamification';
import { youtubeEmbedUrl } from '@/lib/youtube';
import { LessonPracticeGame } from '@/components/learning/games/LessonPracticeGame';

/**
 * Group ordered lesson blocks into bite-sized "steps" — one block per step, so
 * each piece of content is its own screen and its own outline entry
 * (SoloLearn/Duolingo style). A heading is merged with the block right after it
 * so it never sits on a screen alone.
 */
function groupIntoSteps(blocks: LessonBlockDto[]): LessonBlockDto[][] {
  const steps: LessonBlockDto[][] = [];
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (!block) continue;
    const nextBlock = blocks[i + 1];
    if (block.kind === 'heading' && nextBlock) {
      steps.push([block, nextBlock]);
      i++; // the heading pulled in the following block
    } else {
      steps.push([block]);
    }
  }
  return steps;
}

/** localStorage key for the learner's desktop course-outline preference. */
const SIDE_PREF_KEY = 'vn-lesson-side';

/** Strip HTML tags for using authored heading/caption text as a plain label. */
function stripTags(value: string): string {
  return value.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

/** A short outline label for a step: heading, else caption, else first words. */
function stepLabel(blocks: LessonBlockDto[], index: number): string {
  const heading = blocks.find((b) => b.kind === 'heading' && (b.text ?? '').trim());
  if (heading?.text) return stripTags(heading.text);
  const captioned = blocks.find((b) => (b.caption ?? '').trim());
  if (captioned?.caption) return captioned.caption as string;
  const texty = blocks.find((b) => (b.text ?? '').trim());
  if (texty?.text) {
    const words = stripTags(texty.text).split(' ').slice(0, 5).join(' ');
    if (words) return `${words}…`;
  }
  return `${index + 1}`;
}

function lessonOutline(lesson: LearningLessonDto, certificateLabel: string) {
  const contentSteps = groupIntoSteps(lesson.content ?? []);
  return [
    ...contentSteps.map((blocks, index) => ({ label: stepLabel(blocks, index), certificate: false })),
    { label: certificateLabel, certificate: true },
  ];
}

function paragraphs(text: string | undefined | null): string[] {
  return (text ?? '')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}

function looksLikeHtml(text: string | undefined | null): boolean {
  return /<[a-z!/][\s\S]*>/i.test(text ?? '');
}

/** Minimal sanitizer for admin-authored rich text before it is rendered. */
function sanitizeHtml(html: string): string {
  return html
    .replace(/<\/?(script|style|iframe|object|embed|link|meta|form)[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript:/gi, '');
}

/** Render rich-text HTML when present, else fall back to plain paragraphs. */
function RichText({ text, className }: { text: string | undefined; className?: string }) {
  if (looksLikeHtml(text)) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: sanitizeHtml(text ?? '') }} />;
  }
  return (
    <div className={className}>
      {paragraphs(text).map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

/**
 * A hands-on practice activity: a titled task with instructions and optional
 * steps the learner ticks off. Completion is local to the browser — this is
 * practice, not a scored/saved exercise.
 */
/**
 * A plain data table. Wrapped in its own scroll container so a wide table
 * scrolls sideways on a phone instead of forcing the whole lesson to.
 */
function TableBlock({ block }: { block: LessonBlockDto }) {
  const rows = block.rows ?? [];
  if (rows.length === 0) return null;

  const headerRow = block.headerRow !== false;
  const head = headerRow ? rows[0] : null;
  const body = headerRow ? rows.slice(1) : rows;

  return (
    <figure className="vn-w3__block vn-w3__tablewrap">
      <div className="vn-w3__tablescroll">
        <table className="vn-w3__table">
          {head ? (
            <thead>
              <tr>
                {head.map((cell, index) => (
                  <th key={index} scope="col">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {body.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td key={colIndex}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {block.caption ? <figcaption className="vn-w3__caption">{block.caption}</figcaption> : null}
    </figure>
  );
}

function ActivityBlock({ block }: { block: LessonBlockDto }) {
  const t = useT();
  const steps = block.items ?? [];
  const [done, setDone] = useState<Record<number, boolean>>({});
  const [marked, setMarked] = useState(false);
  const allStepsDone = steps.length > 0 && steps.every((_, i) => done[i]);
  const complete = steps.length > 0 ? allStepsDone : marked;

  return (
    <section className={`vn-w3__block vn-w3__activity${complete ? ' is-complete' : ''}`}>
      <div className="vn-w3__activity-head">
        <span className="vn-w3__activity-badge">{t('lesson.activityBadge')}</span>
        <h2>{block.caption || t('lesson.activityTitle')}</h2>
      </div>
      {block.text ? <RichText text={block.text} className="vn-prose" /> : null}
      {steps.length > 0 ? (
        <ul className="vn-w3__activity-steps">
          {steps.map((step, i) => (
            <li key={i}>
              <label className={`vn-w3__activity-step${done[i] ? ' is-done' : ''}`}>
                <input
                  type="checkbox"
                  checked={Boolean(done[i])}
                  onChange={() => setDone((current) => ({ ...current, [i]: !current[i] }))}
                />
                <span>{step}</span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="vn-w3__activity-foot">
        {complete ? (
          <span className="vn-w3__activity-done">{t('lesson.activityDone')}</span>
        ) : steps.length === 0 ? (
          <button
            type="button"
            className="vn-btn vn-btn--secondary vn-btn--small"
            onClick={() => setMarked(true)}
          >
            {t('lesson.activityMarkDone')}
          </button>
        ) : null}
      </div>
    </section>
  );
}

function LessonCertificateStep({
  lesson,
  token,
  user,
  accountNext,
}: {
  lesson: LearningLessonDto;
  token: string | null;
  user: UserDto | null;
  accountNext: string;
}) {
  const t = useT();
  const issuedOn = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <section className="vn-lesson-cert" aria-label={t('cert.lessonHeading')}>
      <div className="vn-success-fireworks" role="status">
        <span className="vn-success-fireworks__burst" aria-hidden="true" />
        <span>
          <b>{t('lesson.successTitle')}</b>
          {t('lesson.successCopy', { lesson: lesson.title })}
        </span>
      </div>

      <div className="vn-cert vn-cert--lesson" role="img" aria-label={t('cert.lessonAria', { lesson: lesson.title })}>
        <div className="vn-cert__inner">
          <p className="vn-cert__brand">
            <span className="vn-cert__brand-mark" aria-hidden="true">
              ◆
            </span>
            Vuganawe — Practical English
          </p>
          <p className="vn-cert__heading">{t('cert.lessonHeading')}</p>
          <p className="vn-cert__presented">{t('cert.presented')}</p>
          <p className="vn-cert__name">{user?.name ?? 'Vuganawe Learner'}</p>
          <p className="vn-cert__for">{t('cert.forCompletingLesson')}</p>
          <p className="vn-cert__course">{lesson.title}</p>
          <p className="vn-cert__meta">
            {lesson.moduleTitle} · {t(`skills.${lesson.moduleSkill}`)} · {t(`levels.${lesson.moduleLevel}`)} ·{' '}
            {t('common.minutes', { count: lesson.durationMinutes })}
            {lesson.quizScore !== null ? ` · ${t('cert.quizScore', { score: lesson.quizScore })}` : ''}
          </p>
          <div className="vn-cert__footer">
            <span className="vn-cert__date">{t('cert.issued', { date: issuedOn })}</span>
            <span className="vn-cert__sig">
              <b>Vuganawe</b>
              vuganawe.com/learn
            </span>
          </div>
        </div>
      </div>

      {!token ? (
        <p className="vn-lesson-cert__note">
          {t('cert.lessonGuestNote')}{' '}
          <Link href={`/account/register?next=${encodeURIComponent(accountNext)}`}>
            {t('common.createFreeAccount')} →
          </Link>
        </p>
      ) : null}
    </section>
  );
}

interface LessonReaderProps {
  lesson: LearningLessonDto;
  /** Ordered lessons of the current module — powers the sidebar + pager. */
  moduleLessons: LearningLessonDto[];
  /** Account mode keeps signed-in learners inside the app shell. */
  hrefMode?: 'site' | 'account';
}

export function LessonReader({ lesson: initialLesson, moduleLessons, hrefMode = 'site' }: LessonReaderProps) {
  const t = useT();
  const router = useRouter();
  const [lesson, setLesson] = useState(initialLesson);
  const [lessons, setLessons] = useState(moduleLessons);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserDto | null>(null);
  // True once we've loaded the learner's real progress (needed before gating).
  const [synced, setSynced] = useState(false);
  const [busy, setBusy] = useState(false);
  // Brief "+10 XP" reward flash when a step is completed.
  const [xpFlash, setXpFlash] = useState<number | null>(null);
  const xpFlashTimer = useRef<number | undefined>(undefined);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [question, setQuestion] = useState('');
  const [askState, setAskState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [courseRating, setCourseRating] = useState<{ moduleId: string; myRating: number | null } | null>(
    null,
  );
  /** Mobile: whether the lesson-contents bottom sheet is open. */
  const [contentsOpen, setContentsOpen] = useState(false);
  /** Desktop: whether the course-outline rail is collapsed for more reading room. */
  const [sideCollapsed, setSideCollapsed] = useState(false);
  const [expandedLessons, setExpandedLessons] = useState<Record<string, boolean>>({});
  const [showCelebration, setShowCelebration] = useState(false);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const savedProgressRef = useRef(initialLesson.progressPercent ?? 0);
  const autoCompletingRef = useRef(false);
  const celebrationTimerRef = useRef<number | null>(null);

  // Restore the learner's desktop rail preference so it persists between lessons.
  useEffect(() => {
    try {
      setSideCollapsed(window.localStorage.getItem(SIDE_PREF_KEY) === 'collapsed');
    } catch {
      /* private mode — leave the outline open */
    }
  }, []);

  const setSidePref = useCallback((collapsed: boolean) => {
    setSideCollapsed(collapsed);
    try {
      window.localStorage.setItem(SIDE_PREF_KEY, collapsed ? 'collapsed' : 'open');
    } catch {
      /* ignore persistence failures */
    }
  }, []);

  const catalogHref = hrefMode === 'account' ? '/account/learn' : '/learn';
  const moduleHref = useCallback(
    (slug: string) => (hrefMode === 'account' ? `/account/learn/${slug}` : `/learn/${slug}`),
    [hrefMode],
  );
  const lessonHref = useCallback(
    (slug: string) =>
      hrefMode === 'account' ? `/account/learn/lessons/${slug}` : `/learn/lessons/${slug}`,
    [hrefMode],
  );

  /** Refetch server truth (per-learner progress, ratings) with the token. */
  const refreshFromServer = useCallback(
    (learnerToken: string) => {
      void getLearningModuleWithAuth(initialLesson.moduleSlug, learnerToken)
        .then((detail) => {
          setLessons(detail.lessons);
          setCourseRating({ moduleId: detail.module.id, myRating: detail.module.myRating });
          const fresh = detail.lessons.find((item) => item.slug === initialLesson.slug);
          if (fresh) setLesson(fresh);
          setSynced(true);
        })
        .catch(() => {
          /* offline — keep the local copy; the sync pill explains the state */
        });
    },
    [initialLesson.moduleSlug, initialLesson.slug],
  );

  // Refresh with the learner's progress once we know they are signed in, and
  // again after the offline queue flushes (server truth after negotiation).
  useEffect(() => {
    const learnerToken = getLearnerToken();
    setToken(learnerToken);
    setUser(learnerToken ? getLearnerUser() : null);
    if (!learnerToken) return;
    refreshFromServer(learnerToken);
    const onFlushed = () => refreshFromServer(learnerToken);
    window.addEventListener('vn-sync-flushed', onFlushed);
    return () => window.removeEventListener('vn-sync-flushed', onFlushed);
  }, [refreshFromServer]);

  useEffect(() => {
    setExpandedLessons((current) => ({ ...current, [lesson.id]: true }));
  }, [lesson.id]);

  useEffect(
    () => () => {
      if (celebrationTimerRef.current) {
        window.clearTimeout(celebrationTimerRef.current);
      }
    },
    [],
  );

  const celebrateLessonCompletion = useCallback(() => {
    setShowCelebration(true);
    if (celebrationTimerRef.current) {
      window.clearTimeout(celebrationTimerRef.current);
    }
    celebrationTimerRef.current = window.setTimeout(() => setShowCelebration(false), 4600);
  }, []);

  const index = lessons.findIndex((item) => item.slug === lesson.slug);
  const prev = index > 0 ? lessons[index - 1] : null;
  const next = index >= 0 && index < lessons.length - 1 ? lessons[index + 1] : null;

  const content = useMemo(() => lesson.content ?? [], [lesson.content]);

  // Bite-sized reading: authored content steps plus a final lesson-certificate step.
  const [stepIndex, setStepIndex] = useState(0);
  const contentSteps = useMemo(() => groupIntoSteps(content), [content]);
  const contentStepCount = contentSteps.length;
  const certificateStepIndex = contentStepCount;
  const stepCount = contentStepCount + 1;
  const useSteps = stepCount > 0;

  // Restart at the first step whenever the lesson changes.
  useEffect(() => {
    setStepIndex(0);
  }, [lesson.id]);

  // ── Gating ────────────────────────────────────────────────────────────────
  // Steps unlock in order: you can revisit completed steps and the current one,
  // but can't skip ahead to steps you haven't reached. Forward is via Continue.
  const reachedStep = lesson.completed
    ? stepCount - 1
    : Math.min(
        Math.max(0, certificateStepIndex - 1),
        Math.floor(((lesson.progressPercent ?? 0) / 100) * stepCount),
      );
  const maxStep = Math.max(stepIndex, reachedStep < 0 ? 0 : reachedStep);
  const stepUnlocked = (i: number) => {
    if (i === certificateStepIndex) return lesson.completed || stepIndex === certificateStepIndex;
    return i <= maxStep;
  };

  // Lessons unlock in order too: a lesson opens once every lesson before it is
  // complete (and completed lessons always stay open). Guests aren't gated.
  const firstIncompleteLesson = lessons.findIndex((item) => !item.completed);
  const lessonUnlocked = (i: number) =>
    !token ||
    lessons[i]?.completed === true ||
    firstIncompleteLesson === -1 ||
    i <= firstIncompleteLesson;

  // Direct-URL guard: a signed-in learner on a lesson they haven't unlocked yet
  // (only known after the auth sync) sees a locked screen instead of the body.
  const currentLocked = Boolean(token) && synced && index >= 0 && !lessonUnlocked(index);

  // Word pairs / sentences derived from this lesson, for the practice game.
  const lessonPairs = useMemo(() => {
    const out: { en: string; rw: string }[] = [];
    for (const word of lesson.vocabulary ?? []) {
      // Accept "english = kinyarwanda" or "english - kinyarwanda" authoring.
      const match = word.split(/\s*[=\-–—:]\s*/);
      if (match.length >= 2 && match[0] && match[1]) {
        out.push({ en: match[0].trim(), rw: match[1].trim() });
      }
    }
    return out;
  }, [lesson.vocabulary]);

  // Short example sentences usable by the sentence-builder practice game.
  const lessonSentences = useMemo(
    () => (lesson.examples ?? []).filter((sentence) => sentence.trim().split(/\s+/).length >= 3),
    [lesson.examples],
  );

  // All quiz questions across every quiz block, scored together.
  const quizQuestions = useMemo(() => {
    const out: { key: string; question: string; options: string[]; answer: string; explanation?: string }[] = [];
    for (const block of content) {
      if (block.kind === 'quiz' && block.questions) {
        block.questions.forEach((q, i) =>
          out.push({ key: `${block.id}:${i}`, question: q.question, options: q.options, answer: q.answer, explanation: q.explanation }),
        );
      }
    }
    return out;
  }, [content]);

  const quizComplete = quizQuestions.length > 0 && quizQuestions.every(({ key }) => answers[key]);
  const correctCount = useMemo(
    () => quizQuestions.filter(({ key, answer }) => answers[key] === answer).length,
    [answers, quizQuestions],
  );
  const score = useMemo(() => {
    if (!quizComplete) return null;
    return Math.round((correctCount / quizQuestions.length) * 100);
  }, [correctCount, quizQuestions.length, quizComplete]);
  const hasQuiz = quizQuestions.length > 0;

  useEffect(() => {
    savedProgressRef.current = lesson.progressPercent ?? 0;
    autoCompletingRef.current = false;
  }, [lesson.id, lesson.progressPercent]);

  useEffect(() => {
    // Step mode drives progress from the step navigator, not scroll depth.
    if (useSteps || !token || lesson.completed) return;
    const main = mainRef.current;
    if (!main) return;
    let raf = 0;

    const saveMilestone = (percent: number, completed: boolean) => {
      if (autoCompletingRef.current || percent <= savedProgressRef.current) return;
      savedProgressRef.current = percent;
      if (completed) autoCompletingRef.current = true;

      saveLessonProgress(lesson.id, token, { progressPercent: percent, completed })
        .then((updated) => {
          setLesson(updated);
          setLessons((current) => current.map((item) => (item.id === updated.id ? updated : item)));
          if (completed) {
            setMessage(t('lesson.autoCompleted'));
            celebrateLessonCompletion();
          }
        })
        .catch((error) => {
          if (isNetworkError(error)) {
            enqueueWrite({
              path: `/learning/me/lessons/${lesson.id}/progress`,
              method: 'PATCH',
              body: { progressPercent: percent, completed },
              description: `Progress ${percent}% on "${lesson.title}"`,
            });
            setLesson((current) => ({
              ...current,
              progressPercent: Math.max(current.progressPercent, percent),
              completed: current.completed || completed,
            }));
            setLessons((current) =>
              current.map((item) =>
                item.id === lesson.id
                  ? {
                      ...item,
                      progressPercent: Math.max(item.progressPercent, percent),
                      completed: item.completed || completed,
                    }
                  : item,
              ),
            );
            if (completed) {
              setMessage(t('sync.savedOffline'));
              celebrateLessonCompletion();
            }
          }
        })
        .finally(() => {
          if (!completed) autoCompletingRef.current = false;
        });
    };

    const measure = () => {
      raf = 0;
      const rect = main.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const start = scrollTop + rect.top;
      const end = start + main.offsetHeight;
      const seen = scrollTop + window.innerHeight - start;
      const total = Math.max(1, end - start);
      const percent = Math.max(0, Math.min(100, Math.round((seen / total) * 100)));
      const complete = !hasQuiz && percent >= 92;
      const milestone = complete
        ? 100
        : percent >= 90
          ? 90
          : percent >= 75
            ? 75
            : percent >= 50
              ? 50
              : percent >= 25
                ? 25
                : 0;
      if (milestone > 0) saveMilestone(milestone, complete);
    };

    const scheduleMeasure = () => {
      if (!raf) raf = window.requestAnimationFrame(measure);
    };

    scheduleMeasure();
    window.addEventListener('scroll', scheduleMeasure, { passive: true });
    window.addEventListener('resize', scheduleMeasure);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', scheduleMeasure);
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [celebrateLessonCompletion, hasQuiz, lesson.completed, lesson.id, lesson.title, t, token, useSteps]);

  /** Persist a progress milestone (used by the step navigator). Offline-safe. */
  const persistProgress = useCallback(
    (percent: number, completed: boolean) => {
      if (!token || lesson.completed) return;
      if (!completed && percent <= savedProgressRef.current) return;
      savedProgressRef.current = Math.max(savedProgressRef.current, percent);
      saveLessonProgress(lesson.id, token, { progressPercent: percent, completed })
        .then((updated) => {
          setLesson(updated);
          setLessons((current) => current.map((item) => (item.id === updated.id ? updated : item)));
          if (completed) {
            setMessage(t('lesson.autoCompleted'));
            celebrateLessonCompletion();
          }
        })
        .catch((error) => {
          if (isNetworkError(error)) {
            enqueueWrite({
              path: `/learning/me/lessons/${lesson.id}/progress`,
              method: 'PATCH',
              body: { progressPercent: percent, completed },
              description: `Progress ${percent}% on "${lesson.title}"`,
            });
            setLesson((current) => ({
              ...current,
              progressPercent: Math.max(current.progressPercent, percent),
              completed: current.completed || completed,
            }));
            if (completed) {
              setMessage(t('sync.savedOffline'));
              celebrateLessonCompletion();
            }
          }
        });
    },
    [celebrateLessonCompletion, lesson.completed, lesson.id, lesson.title, t, token],
  );

  /** Move to a step, play a cue, scroll up, and save the lesson-level milestone. */
  const goToStep = useCallback(
    (target: number) => {
      const clamped = Math.max(0, Math.min(stepCount - 1, target));
      setStepIndex(clamped);
      playStep();
      mainRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const percent = stepCount > 0 ? Math.round(((clamped + 1) / stepCount) * 100) : 0;
      const complete = !hasQuiz && clamped === certificateStepIndex;
      persistProgress(complete ? 100 : percent, complete);
    },
    [certificateStepIndex, hasQuiz, persistProgress, stepCount],
  );

  /** Jump to a step from the outline — allowed only for unlocked (reached) steps. */
  function goToStepGated(target: number) {
    if (!stepUnlocked(target)) return;
    setContentsOpen(false);
    goToStep(target);
  }

  /** Brief "+XP" reward flash shown when a step is completed. */
  function flashXp(amount: number) {
    if (amount <= 0) return;
    setXpFlash(amount);
    window.clearTimeout(xpFlashTimer.current);
    xpFlashTimer.current = window.setTimeout(() => setXpFlash(null), 1600);
  }

  /** Continue to the next step, awarding XP for the step just completed. */
  function continueStep() {
    const result = awardStepXp(lesson.id, stepIndex);
    flashXp(result.gained);
    goToStep(stepIndex + 1);
  }

  /** Apply the completed state locally (optimistic / offline-queued writes). */
  function markCompleteLocally() {
    setLesson((current) => ({ ...current, completed: true, progressPercent: 100 }));
    setLessons((current) =>
      current.map((item) =>
        item.id === lesson.id ? { ...item, completed: true, progressPercent: 100 } : item,
      ),
    );
  }

  async function markComplete(goNext = false) {
    // Guests and already-completed lessons: nothing to save, just move on.
    if (!token || lesson.completed) {
      if (goNext && next) router.push(lessonHref(next.slug));
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const updated = await saveLessonProgress(lesson.id, token, { completed: true, progressPercent: 100 });
      setLesson(updated);
      setLessons((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      celebrateLessonCompletion();
      if (goNext && next) {
        router.push(lessonHref(next.slug));
        return;
      }
      setMessage(t('lesson.markedComplete'));
    } catch (error) {
      if (isNetworkError(error)) {
        // Offline: local truth now, queue the write, sync negotiates later.
        enqueueWrite({
          path: `/learning/me/lessons/${lesson.id}/progress`,
          method: 'PATCH',
          body: { completed: true, progressPercent: 100 },
          description: `Complete "${lesson.title}"`,
        });
        markCompleteLocally();
        celebrateLessonCompletion();
        if (goNext && next) {
          router.push(lessonHref(next.slug));
          return;
        }
        setMessage(t('sync.savedOffline'));
      } else {
        setMessage(error instanceof Error ? error.message : t('lesson.saveError'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function toggleFavorite() {
    if (!token) return;
    const nextValue = !lesson.favorite;
    setBusy(true);
    setMessage('');
    try {
      await saveLessonFavorite(lesson.id, token, nextValue);
      setLesson((current) => ({ ...current, favorite: nextValue }));
    } catch (error) {
      if (isNetworkError(error)) {
        enqueueWrite({
          path: `/learning/me/lessons/${lesson.id}/favorite`,
          method: 'PATCH',
          body: { favorite: nextValue },
          description: `Save "${lesson.title}"`,
        });
        setLesson((current) => ({ ...current, favorite: nextValue }));
        setMessage(t('sync.savedOffline'));
      } else {
        setMessage(error instanceof Error ? error.message : t('lesson.savedError'));
      }
    } finally {
      setBusy(false);
    }
  }

  /** Save a 1–5 star course rating (optimistic; queues offline). */
  function rateCourse(rating: number) {
    if (!token || !courseRating) return;
    const previous = courseRating.myRating;
    setCourseRating({ ...courseRating, myRating: rating });
    saveCourseRating(courseRating.moduleId, token, rating).catch((error) => {
      if (isNetworkError(error)) {
        enqueueWrite({
          path: `/learning/me/modules/${courseRating.moduleId}/rating`,
          method: 'PATCH',
          body: { rating },
          description: `Rate "${lesson.moduleTitle}" ${rating}★`,
        });
        setMessage(t('sync.savedOffline'));
      } else {
        setCourseRating((current) => (current ? { ...current, myRating: previous } : current));
      }
    });
  }

  /** Send a lesson question to the coach — lands in the admin Inquiries list. */
  async function askCoach() {
    const user = getLearnerUser();
    if (!user || !question.trim()) return;
    setAskState('sending');
    try {
      await submitInquiry({
        type: 'GENERAL',
        name: user.name,
        email: user.email,
        message: `Question about the lesson “${lesson.title}” (${lesson.moduleTitle}):\n\n${question.trim()}`,
        sourcePage: `/learn/lessons/${lesson.slug}`,
      });
      setQuestion('');
      setAskState('sent');
    } catch {
      setAskState('error');
    }
  }

  async function submitQuiz() {
    if (score === null) return;
    setQuizSubmitted(true);
    if (!token) return;
    setBusy(true);
    setMessage('');
    try {
      const updated = await saveQuizAttempt(lesson.id, token, { score, answers });
      setLesson(updated);
      setLessons((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setMessage(t('lesson.quizSaved', { score }));
      if (updated.completed && !lesson.completed) celebrateLessonCompletion();
    } catch (error) {
      if (isNetworkError(error)) {
        enqueueWrite({
          path: `/learning/me/lessons/${lesson.id}/quiz-attempts`,
          method: 'POST',
          body: { score, answers },
          description: `Quiz ${score}% on "${lesson.title}"`,
        });
        markCompleteLocally();
        setLesson((current) => ({ ...current, quizScore: score }));
        celebrateLessonCompletion();
        setMessage(t('sync.savedOffline'));
      } else {
        setMessage(error instanceof Error ? error.message : t('lesson.quizError'));
      }
    } finally {
      setBusy(false);
    }
  }

  function renderQuizBlock(block: LessonBlockDto) {
    const questions = block.questions ?? [];
    if (questions.length === 0) return null;
    return (
      <section key={block.id} className="vn-w3__block vn-w3__quiz">
        <h2>{block.caption || t('lesson.checkYourself')}</h2>
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {questions.map((item, qi) => {
            const key = `${block.id}:${qi}`;
            const chosen = answers[key];
            return (
              <fieldset key={key} className="vn-w3__qcard">
                <legend>
                  {qi + 1}. {item.question}
                </legend>
                <div className="vn-w3__options">
                  {item.options.map((option) => {
                    const isChosen = chosen === option;
                    const tone = quizSubmitted
                      ? option === item.answer
                        ? ' is-correct'
                        : isChosen
                          ? ' is-wrong'
                          : ''
                      : '';
                    return (
                      <label key={option} className={`vn-w3__option${tone}`}>
                        <input
                          type="radio"
                          name={key}
                          value={option}
                          checked={isChosen}
                          disabled={quizSubmitted}
                          onChange={() => setAnswers((current) => ({ ...current, [key]: option }))}
                        />
                        <span>{option}</span>
                        {quizSubmitted && option === item.answer ? (
                          <span className="vn-w3__option-tag">{t('lesson.correctTag')}</span>
                        ) : null}
                      </label>
                    );
                  })}
                </div>
                {quizSubmitted && item.explanation ? (
                  <p className="vn-w3__explain">{item.explanation}</p>
                ) : null}
              </fieldset>
            );
          })}
        </div>
      </section>
    );
  }

  function renderBlock(block: LessonBlockDto) {
    switch (block.kind) {
      case 'heading':
        return (
          <h2 key={block.id} className="vn-w3__blockheading">
            {block.text}
          </h2>
        );
      case 'text':
        return <RichText key={block.id} text={block.text} className="vn-prose vn-w3__textblock" />;
      case 'callout':
        return (
          <div
            key={block.id}
            className={`vn-w3__note vn-w3__block${block.tone === 'task' ? ' vn-w3__note--task' : ''}`}
            style={block.bg ? { background: block.bg, borderLeftColor: block.bg } : undefined}
          >
            <RichText text={block.text} className="vn-prose" />
          </div>
        );
      case 'table':
        return <TableBlock key={block.id} block={block} />;
      case 'activity':
        return <ActivityBlock key={block.id} block={block} />;
      case 'image':
        return block.url ? (
          <figure key={block.id} className="vn-w3__figure">
            <img src={block.url} alt={block.caption ?? ''} className="vn-w3__media" />
            {block.caption ? <figcaption className="vn-w3__caption">{block.caption}</figcaption> : null}
          </figure>
        ) : null;
      case 'video':
        return block.url ? (
          <figure key={block.id} className="vn-w3__figure">
            <video src={block.url} controls className="vn-w3__media" style={{ background: '#000' }} />
            {block.caption ? <figcaption className="vn-w3__caption">{block.caption}</figcaption> : null}
          </figure>
        ) : null;
      case 'youtube': {
        const embed = youtubeEmbedUrl(block.url);
        if (!embed) return null;
        return (
          <section key={block.id} className="vn-w3__block vn-embed-block">
            {block.caption ? <h2 className="vn-embed-block__title">{block.caption}</h2> : null}
            <div className="vn-embed">
              <iframe
                src={embed}
                title={block.caption || 'YouTube video'}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </section>
        );
      }
      case 'audio':
        return block.url ? (
          <figure key={block.id} className="vn-w3__figure">
            <audio src={block.url} controls className="vn-w3__media" />
            {block.caption ? <figcaption className="vn-w3__caption">{block.caption}</figcaption> : null}
          </figure>
        ) : null;
      case 'file':
        return block.url ? (
          <div key={block.id} className="vn-w3__block">
            <a href={block.url} target="_blank" rel="noreferrer" className="vn-btn vn-btn--secondary">
              ⬇ {block.caption || t('lesson.downloadFile')}
            </a>
          </div>
        ) : null;
      case 'gapfill':
        return <GapFillBlock key={block.id} block={block} />;
      case 'matching':
        return <MatchingBlock key={block.id} block={block} />;
      case 'speaking':
        return <SpeakingBlock key={block.id} block={block} />;
      case 'dictation':
        return <DictationBlock key={block.id} block={block} />;
      case 'vocabulary': {
        const entries: VocabEntryDto[] =
          block.entries && block.entries.length > 0
            ? block.entries
            : (block.items ?? []).map((term) => ({ term }));
        if (entries.length === 0) return null;
        const hasMeanings = entries.some((entry) => entry.meaning || entry.example);
        return (
          <section key={block.id} className="vn-w3__block">
            <h2>{block.caption || t('lesson.keyVocabulary')}</h2>
            {hasMeanings ? (
              <dl className="vn-vocablist">
                {entries.map((entry) => (
                  <div key={entry.term} className="vn-vocablist__row">
                    <dt className="vn-vocablist__term">{entry.term}</dt>
                    <dd className="vn-vocablist__meaning">
                      {entry.meaning ? <span>{entry.meaning}</span> : null}
                      {entry.example ? (
                        <em className="vn-vocablist__example">“{entry.example}”</em>
                      ) : null}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <div className="vn-taglist">
                {entries.map((entry) => (
                  <span key={entry.term} className="vn-badge vn-badge--neutral">
                    {entry.term}
                  </span>
                ))}
              </div>
            )}
          </section>
        );
      }
      case 'quiz':
        return renderQuizBlock(block);
      default:
        return null;
    }
  }

  const accountNext = lessonHref(lesson.slug);
  const completedCount = lessons.filter((item) => item.completed).length;

  // Progressive rendering: which blocks show now, and are we at the end?
  const isCertificateStep = useSteps && stepIndex === certificateStepIndex;
  const contentLastStep = !useSteps || stepIndex >= Math.max(0, contentStepCount - 1);
  const onLastStep = !useSteps || stepIndex >= stepCount - 1;
  const nextStepIsCertificate = useSteps && stepIndex + 1 === certificateStepIndex;
  const certificateBlocked = nextStepIsCertificate && hasQuiz && !lesson.completed;
  const visibleBlocks = useSteps && !isCertificateStep ? contentSteps[stepIndex] ?? [] : content;
  const visibleHasQuiz = visibleBlocks.some((block) => block.kind === 'quiz');
  const canPractiseGame = lessonPairs.length >= 3 || lessonSentences.length >= 2;
  const certificateStepLabel = t('lesson.lessonCertificateStep');

  if (currentLocked) {
    const target = firstIncompleteLesson >= 0 ? lessons[firstIncompleteLesson] : null;
    return (
      <div className="vn-w3">
        <div className="vn-w3__locked">
          <span className="vn-w3__locked-icon" aria-hidden="true">
            🔒
          </span>
          <h1>{t('lesson.lockedTitle')}</h1>
          <p>{t('lesson.lockedBody')}</p>
          <div className="vn-w3__locked-actions">
            {target ? (
              <Link href={lessonHref(target.slug)} className="vn-btn vn-btn--primary">
                {t('lesson.goToCurrent')}
              </Link>
            ) : null}
            <Link href={moduleHref(lesson.moduleSlug)} className="vn-btn vn-btn--secondary">
              {t('pager.courseOverview')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vn-w3">
      {xpFlash !== null ? (
        <div className="vn-xp-flash" role="status" aria-live="polite">
          +{xpFlash} XP
        </div>
      ) : null}
      {showCelebration ? (
        <div className="vn-fireworks-toast" role="status">
          <span className="vn-fireworks-toast__icon" aria-hidden="true">
            ✦
          </span>
          <span>
            <b>{t('lesson.successTitle')}</b>
            {t('lesson.successToast')}
          </span>
        </div>
      ) : null}
      <div className={`vn-w3__body${sideCollapsed ? ' is-side-collapsed' : ''}`}>
        <aside
          className={`vn-w3__side${contentsOpen ? ' is-open' : ''}`}
          aria-label={t('lesson.courseContent')}
        >
          <div className="vn-w3__side-sheethead">
            <span className="vn-w3__side-grab" aria-hidden="true" />
            <span className="vn-w3__side-sheettitle">{t('lesson.courseContent')}</span>
            <button
              type="button"
              className="vn-w3__side-close"
              onClick={() => setContentsOpen(false)}
              aria-label={t('common.back')}
            >
              ✕
            </button>
          </div>
          <div className="vn-w3__side-head">
            <button
              type="button"
              className="vn-w3__side-collapse"
              onClick={() => setSidePref(true)}
              aria-label={t('lesson.hideContents')}
              title={t('lesson.hideContents')}
            >
              «
            </button>
            <p className="vn-w3__side-eyebrow">{t('lesson.courseContent')}</p>
            <Link href={moduleHref(lesson.moduleSlug)} className="vn-w3__side-course">
              {lesson.moduleTitle}
            </Link>
            {token ? (
              <>
                <div className="vn-progress vn-w3__side-progress">
                  <div
                    className="vn-progress__bar"
                    style={{
                      width: `${lessons.length ? Math.round((completedCount / lessons.length) * 100) : 0}%`,
                    }}
                  />
                </div>
                <p className="vn-w3__side-progresslabel">
                  {t('lesson.lessonsDone', { completed: completedCount, total: lessons.length })}
                </p>
              </>
            ) : null}
          </div>
          <ol className="vn-w3__side-list">
            {lessons.map((item, itemIndex) => {
              const isActive = item.slug === lesson.slug;
              const unlocked = lessonUnlocked(itemIndex);
              const expanded = Boolean(expandedLessons[item.id]);
              const outline = lessonOutline(item, certificateStepLabel);
              const inner = (
                <>
                  <span
                    className={`vn-w3__side-num${item.completed ? ' is-done' : ''}${
                      !unlocked ? ' is-locked' : ''
                    }`}
                    aria-hidden="true"
                  >
                    {item.completed ? '✓' : unlocked ? itemIndex + 1 : '🔒'}
                  </span>
                  <span className="vn-w3__side-body">
                    <span className="vn-w3__side-name">{item.title}</span>
                    <span className="vn-w3__side-mins">
                      {unlocked
                        ? t('common.minutes', { count: item.durationMinutes })
                        : t('lesson.locked')}
                    </span>
                  </span>
                </>
              );
              return (
                <li key={item.id} className="vn-w3__side-item">
                  <div className="vn-w3__side-row">
                    {unlocked ? (
                      <Link
                        href={lessonHref(item.slug)}
                        className={`vn-w3__side-link${isActive ? ' is-active' : ''}`}
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => setContentsOpen(false)}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <span
                        className="vn-w3__side-link is-locked"
                        aria-disabled="true"
                        title={t('lesson.lockedHint')}
                      >
                        {inner}
                      </span>
                    )}
                    <button
                      type="button"
                      className="vn-w3__side-toggle"
                      aria-expanded={expanded}
                      aria-label={expanded ? t('lesson.closeSteps') : t('lesson.expandSteps')}
                      disabled={!unlocked}
                      onClick={() =>
                        setExpandedLessons((current) => ({
                          ...current,
                          [item.id]: !current[item.id],
                        }))
                      }
                    >
                      {expanded ? '−' : '+'}
                    </button>
                  </div>

                  {expanded ? (
                    <ol className="vn-w3__outline">
                      {outline.map((entry, i) => {
                        const current = isActive && i === stepIndex;
                        const open = isActive ? stepUnlocked(i) : unlocked;
                        const mark = entry.certificate ? '🎖' : current ? '●' : open ? '✓' : '🔒';
                        const className = `vn-w3__outline-item${current ? ' is-current' : ''}${
                          open && !current ? ' is-done' : ''
                        }${!open ? ' is-locked' : ''}${entry.certificate ? ' is-certificate' : ''}`;
                        return (
                          <li key={i}>
                            {isActive ? (
                              <button
                                type="button"
                                className={className}
                                disabled={!open}
                                aria-current={current ? 'step' : undefined}
                                onClick={() => goToStepGated(i)}
                              >
                                <span className="vn-w3__outline-mark" aria-hidden="true">
                                  {mark}
                                </span>
                                <span className="vn-w3__outline-label">{entry.label}</span>
                              </button>
                            ) : unlocked ? (
                              <Link href={lessonHref(item.slug)} className={className}>
                                <span className="vn-w3__outline-mark" aria-hidden="true">
                                  {mark}
                                </span>
                                <span className="vn-w3__outline-label">{entry.label}</span>
                              </Link>
                            ) : (
                              <span className={className} aria-disabled="true">
                                <span className="vn-w3__outline-mark" aria-hidden="true">
                                  {mark}
                                </span>
                                <span className="vn-w3__outline-label">{entry.label}</span>
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </aside>
        <button
          type="button"
          className={`vn-w3__scrim${contentsOpen ? ' is-open' : ''}`}
          aria-hidden={!contentsOpen}
          tabIndex={-1}
          onClick={() => setContentsOpen(false)}
        />

        {sideCollapsed ? (
          <button
            type="button"
            className="vn-w3__side-reopen"
            onClick={() => setSidePref(false)}
            aria-label={t('lesson.showContents')}
            title={t('lesson.showContents')}
          >
            <span className="vn-w3__side-reopen-icon" aria-hidden="true">
              ☰
            </span>
            <span className="vn-w3__side-reopen-label">{t('lesson.courseContent')}</span>
          </button>
        ) : null}

        {/* Region, not a landmark: the page's single <main> lives in the site
            layout, so this content column is a labelled region instead. */}
        <div className="vn-w3__main" role="region" aria-label="Lesson content" ref={mainRef}>
          <nav className="vn-w3__crumbs" aria-label="Breadcrumb">
            <Link href={catalogHref}>{t('lesson.crumbLearn')}</Link>
            <span aria-hidden="true">›</span>
            <Link href={moduleHref(lesson.moduleSlug)}>{lesson.moduleTitle}</Link>
            {index >= 0 ? (
              <span className="vn-w3__crumbs-pos">
                {t('lesson.position', { current: index + 1, total: lessons.length })}
              </span>
            ) : null}
          </nav>

          <p className="vn-w3__eyebrow">
            {t(`skills.${lesson.moduleSkill}`)} · {t(`levels.${lesson.moduleLevel}`)} ·{' '}
            {t('common.minutes', { count: lesson.durationMinutes })}
          </p>
          <h1 className="vn-w3__title">{lesson.title}</h1>
          <p className="vn-w3__lede">{lesson.introduction}</p>

          {lesson.imageUrl ? (
            <img src={lesson.imageUrl} alt="" className="vn-w3__cover" />
          ) : null}

          {useSteps ? (
            <div className="vn-step">
              <div
                className="vn-step__progress"
                role="progressbar"
                aria-valuenow={stepIndex + 1}
                aria-valuemin={1}
                aria-valuemax={stepCount}
              >
                <div
                  className="vn-step__progress-bar"
                  style={{ width: `${Math.round(((stepIndex + 1) / stepCount) * 100)}%` }}
                />
              </div>
              <p className="vn-step__pos">
                {t('lesson.stepPosition', { current: stepIndex + 1, total: stepCount })}
              </p>
              <div className="vn-step__panel">
                {isCertificateStep ? (
                  <LessonCertificateStep
                    lesson={lesson}
                    token={token}
                    user={user}
                    accountNext={accountNext}
                  />
                ) : (
                  visibleBlocks.map((block) => renderBlock(block))
                )}
              </div>
            </div>
          ) : (
            content.map((block) => renderBlock(block))
          )}

          {quizQuestions.length > 0 && (!useSteps || visibleHasQuiz) ? (
            <div className="vn-w3__quiz-foot">
              {quizSubmitted && score !== null ? (
                <div className="vn-w3__quiz-result" role="status">
                  <div className="vn-w3__quiz-score-big">{score}%</div>
                  <div className="vn-w3__quiz-result-body">
                    <p className="vn-w3__quiz-result-line">
                      {t('lesson.resultSummary', { correct: correctCount, total: quizQuestions.length })}
                    </p>
                    <p className="vn-w3__quiz-result-sub">
                      {score === 100
                        ? t('lesson.resultPerfect')
                        : score >= 60
                          ? t('lesson.resultPass')
                          : t('lesson.resultRetry')}
                      {!token ? t('lesson.signInToSave') : ''}
                    </p>
                  </div>
                </div>
              ) : null}
              <div className="vn-w3__quiz-actions">
                <button
                  type="button"
                  className="vn-btn vn-btn--primary"
                  disabled={!quizComplete || busy || quizSubmitted}
                  onClick={submitQuiz}
                >
                  {quizSubmitted ? t('lesson.submitted') : t('lesson.submitAnswers')}
                </button>
                {quizSubmitted ? (
                  <button
                    type="button"
                    className="vn-btn vn-btn--ghost vn-btn--small"
                    onClick={() => {
                      setAnswers({});
                      setQuizSubmitted(false);
                    }}
                  >
                    {t('common.tryAgain')}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {useSteps ? (
            <div className="vn-step__nav">
              <button
                type="button"
                className="vn-btn vn-btn--ghost vn-btn--small"
                disabled={stepIndex === 0}
                onClick={() => goToStep(stepIndex - 1)}
              >
                {t('lesson.stepBack')}
              </button>
              {!onLastStep ? (
                <button
                  type="button"
                  className="vn-btn vn-btn--primary"
                  disabled={certificateBlocked}
                  title={certificateBlocked ? t('lesson.submitQuizToUnlockCertificate') : undefined}
                  onClick={continueStep}
                >
                  {nextStepIsCertificate ? t('lesson.viewLessonCertificate') : t('lesson.continue')}
                </button>
              ) : (
                <span className="vn-step__done">{t('lesson.stepAllDone')}</span>
              )}
            </div>
          ) : null}

          {contentLastStep && !isCertificateStep && canPractiseGame ? (
            <section className="vn-w3__block">
              <h2>{t('lesson.practiceGame')}</h2>
              <LessonPracticeGame pairs={lessonPairs} sentences={lessonSentences} />
            </section>
          ) : null}

          {contentLastStep && !isCertificateStep && lesson.resources.length > 0 ? (
            <section className="vn-w3__block">
              <h2>{t('lesson.extraPractice')}</h2>
              <ul className="vn-w3__resources">
                {lesson.resources.map((resource) => (
                  <li key={resource.id}>
                    <Link href={`/resources/${resource.slug}`} className="vn-w3__resource">
                      <span className="vn-w3__resource-type">{RESOURCE_TYPE_LABELS[resource.type]}</span>
                      <span className="vn-w3__resource-body">
                        <span className="vn-w3__resource-title">{resource.title}</span>
                        <span className="vn-w3__resource-summary">{resource.summary}</span>
                      </span>
                      <span className="vn-w3__resource-go" aria-hidden="true">
                        {t('lesson.open')}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {contentLastStep && !isCertificateStep && token && lesson.feedback.length > 0 ? (
            <section className="vn-w3__block">
              <h2>{t('lesson.coachFeedback')}</h2>
              {lesson.feedback.map((item) => (
                <div key={item.id} className="vn-w3__note">
                  {item.message}
                  {item.teacherName ? <em> — {item.teacherName}</em> : null}
                </div>
              ))}
            </section>
          ) : null}

          {contentLastStep && !isCertificateStep ? (
          <section className="vn-w3__block vn-ask">
            <h2>{t('ask.title')}</h2>
            {!token ? (
              <p className="vn-ask__note">
                {t('ask.guestNote')}{' '}
                <Link href={`/account/login?next=${encodeURIComponent(accountNext)}`}>
                  {t('common.signIn')} →
                </Link>
              </p>
            ) : askState === 'sent' ? (
              <p className="vn-ask__note vn-ask__note--sent">
                {t('ask.sent')}
                <button
                  type="button"
                  className="vn-btn vn-btn--ghost vn-btn--small"
                  onClick={() => setAskState('idle')}
                >
                  {t('ask.askAnother')}
                </button>
              </p>
            ) : (
              <div className="vn-ask__form">
                <textarea
                  className="vn-textarea"
                  rows={3}
                  placeholder={t('ask.placeholder')}
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                />
                <div className="vn-ask__foot">
                  <button
                    type="button"
                    className="vn-btn vn-btn--primary vn-btn--small"
                    disabled={askState === 'sending' || !question.trim()}
                    onClick={() => void askCoach()}
                  >
                    {askState === 'sending' ? t('ask.sending') : t('ask.send')}
                  </button>
                  {askState === 'error' ? <span className="vn-ask__error">{t('ask.error')}</span> : null}
                </div>
              </div>
            )}
          </section>
          ) : null}

          {onLastStep && token && lessons.length > 0 && lessons.every((item) => item.completed) ? (
            <div className="vn-w3__complete">
              <p className="vn-w3__complete-title">{t('complete.title')}</p>
              <p className="vn-w3__complete-copy">
                {t('complete.copy', { course: lesson.moduleTitle })}
              </p>
              {courseRating ? (
                <div className="vn-rate" role="group" aria-label={t('complete.howWasIt')}>
                  <span className="vn-rate__label">
                    {courseRating.myRating ? t('complete.yourRating') : t('complete.howWasIt')}
                  </span>
                  <span className="vn-rate__stars">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`vn-rate__star${
                          courseRating.myRating !== null && value <= courseRating.myRating
                            ? ' is-on'
                            : ''
                        }`}
                        aria-label={`${value} ★`}
                        onClick={() => rateCourse(value)}
                      >
                        ★
                      </button>
                    ))}
                  </span>
                </div>
              ) : null}
              <div className="vn-w3__complete-actions">
                <Link
                  href={
                    hrefMode === 'account'
                      ? `/account/certificates/${lesson.moduleSlug}`
                      : `/learn/${lesson.moduleSlug}/certificate`
                  }
                  className="vn-btn vn-btn--primary vn-btn--small"
                >
                  {t('complete.viewCertificate')}
                </Link>
                <Link href={catalogHref} className="vn-btn vn-btn--secondary vn-btn--small">
                  {t('complete.findNext')}
                </Link>
              </div>
            </div>
          ) : null}

          {onLastStep ? (
          <div className="vn-w3__pagercards">
            {prev ? (
              <Link href={lessonHref(prev.slug)} className="vn-w3__pagercard">
                <span className="vn-w3__pagercard-dir">{t('pager.previousLesson')}</span>
                <span className="vn-w3__pagercard-title">{prev.title}</span>
              </Link>
            ) : (
              <Link href={moduleHref(lesson.moduleSlug)} className="vn-w3__pagercard">
                <span className="vn-w3__pagercard-dir">{t('pager.courseOverview')}</span>
                <span className="vn-w3__pagercard-title">{lesson.moduleTitle}</span>
              </Link>
            )}
            {next ? (
              token && !lesson.completed ? (
                <span
                  className="vn-w3__pagercard vn-w3__pagercard--next is-locked"
                  aria-disabled="true"
                >
                  <span className="vn-w3__pagercard-dir">🔒 {t('pager.nextLesson')}</span>
                  <span className="vn-w3__pagercard-title">{t('lesson.finishToContinue')}</span>
                </span>
              ) : (
                <Link
                  href={lessonHref(next.slug)}
                  className="vn-w3__pagercard vn-w3__pagercard--next"
                >
                  <span className="vn-w3__pagercard-dir">{t('pager.nextLesson')}</span>
                  <span className="vn-w3__pagercard-title">{next.title}</span>
                </Link>
              )
            ) : (
              <Link
                href={moduleHref(lesson.moduleSlug)}
                className="vn-w3__pagercard vn-w3__pagercard--next"
              >
                <span className="vn-w3__pagercard-dir">{t('pager.courseComplete')}</span>
                <span className="vn-w3__pagercard-title">
                  {t('pager.backTo', { course: lesson.moduleTitle })}
                </span>
              </Link>
            )}
          </div>
          ) : null}
        </div>

        <aside className="vn-w3__rail" aria-label={t('rail.yourProgress')}>
          <div className="vn-w3__progress">
            <p className="vn-w3__progress-title">{t('rail.yourProgress')}</p>
            {token ? (
              <>
                <div className="vn-progress" style={{ marginBottom: 'var(--space-3)' }}>
                  <div
                    className="vn-progress__bar"
                    style={{ width: `${lesson.completed ? 100 : lesson.progressPercent}%` }}
                  />
                </div>
                <p className="vn-w3__progress-meta">
                  {lesson.completed
                    ? t('rail.completed')
                    : t('rail.percentComplete', { percent: lesson.progressPercent })}
                  {lesson.quizScore !== null
                    ? ` · ${t('rail.quizScore', { score: lesson.quizScore })}`
                    : ''}
                </p>
                {!lesson.completed ? (
                  <p className="vn-w3__progress-msg">{t('rail.autoProgress')}</p>
                ) : null}
                <div className="vn-w3__progress-actions">
                  {next ? (
                    <button
                      type="button"
                      className="vn-btn vn-btn--primary vn-btn--small"
                      disabled={busy || !lesson.completed}
                      title={!lesson.completed ? t('lesson.finishToContinue') : undefined}
                      onClick={() => lesson.completed && router.push(lessonHref(next.slug))}
                    >
                      {t('rail.nextLesson')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="vn-btn vn-btn--secondary vn-btn--small"
                    disabled={busy}
                    onClick={toggleFavorite}
                  >
                    {lesson.favorite ? t('rail.savedStar') : t('rail.saveLesson')}
                  </button>
                </div>
                {message ? <p className="vn-w3__progress-msg">{message}</p> : null}
                <Link href="/account/learn" className="vn-w3__progress-link">
                  {t('rail.goToMyLearning')}
                </Link>
              </>
            ) : (
              <>
                <p className="vn-w3__progress-meta">{t('rail.guestCopy')}</p>
                <div className="vn-w3__progress-actions">
                  <Link
                    href={`/account/register?next=${encodeURIComponent(accountNext)}`}
                    className="vn-btn vn-btn--primary vn-btn--small"
                  >
                    {t('common.createFreeAccount')}
                  </Link>
                </div>
                <p className="vn-w3__progress-msg">
                  {t('rail.alreadyHaveAccount')}{' '}
                  <Link href={`/account/login?next=${encodeURIComponent(accountNext)}`}>
                    {t('common.signIn')}
                  </Link>
                </p>
              </>
            )}
          </div>
        </aside>
      </div>

      {/* Mobile-only sticky action bar — thumb-reachable primary CTA + contents. */}
      <div className="vn-w3__mobilebar">
        <button
          type="button"
          className="vn-w3__mobilebar-contents"
          aria-expanded={contentsOpen}
          onClick={() => setContentsOpen((open) => !open)}
        >
          <span className="vn-w3__mobilebar-contents-icon" aria-hidden="true">
            ☰
          </span>
          <span className="vn-w3__mobilebar-contents-label">
            {token
              ? t('lesson.lessonsDone', { completed: completedCount, total: lessons.length })
              : t('lesson.courseContent')}
          </span>
        </button>
        {token ? (
          next ? (
            <button
              type="button"
              className="vn-btn vn-btn--primary vn-w3__mobilebar-cta"
              disabled={busy || !lesson.completed}
              title={!lesson.completed ? t('lesson.finishToContinue') : undefined}
              onClick={() => lesson.completed && router.push(lessonHref(next.slug))}
            >
              {t('rail.nextLesson')}
            </button>
          ) : (
            <Link href={moduleHref(lesson.moduleSlug)} className="vn-btn vn-btn--primary vn-w3__mobilebar-cta">
              {t('pager.courseOverview')}
            </Link>
          )
        ) : (
          <Link
            href={`/account/register?next=${encodeURIComponent(accountNext)}`}
            className="vn-btn vn-btn--primary vn-w3__mobilebar-cta"
          >
            {t('common.createFreeAccount')}
          </Link>
        )}
      </div>
    </div>
  );
}
